import type { CrmCommand, CrmMutation, CrmSnapshot } from "@ohf/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../auth/SessionProvider.js";
import type { ApiClient } from "../../lib/api.js";
import type { Me } from "../../lib/me.js";

export function crmIdentity(me: Me | null) { return JSON.stringify([me?.userId, me?.authUserId, me?.party?.id, me?.scope, me?.role, [...(me?.capabilities ?? [])].sort()]); }
export const crmKey = (identity: string, practiceId = "") => ["crm", identity, practiceId] as const;
export const crmEndpoint = (practiceId = "") => `/crm${practiceId ? `?${new URLSearchParams({ practiceId })}` : ""}`;
export async function sendCrmCommand(api: ApiClient, command: CrmCommand): Promise<CrmMutation> {
  const result = await api.post<CrmMutation>("/crm/commands", command);
  if (result.snapshot.mode !== "staging_crm" || !result.entityId) throw new Error("Unconfirmed CRM response");
  const target = "leadId" in command && command.kind !== "convert_lead" ? command.leadId
    : command.kind === "update_opportunity" || command.kind === "move_opportunity" ? command.opportunityId
    : "taskId" in command ? command.taskId : "noteId" in command ? command.noteId : command.kind === "save_pipeline" ? command.pipelineId : undefined;
  if (target && result.entityId !== target) throw new Error("Mismatched CRM action response");
  const records = command.kind === "save_pipeline" ? result.snapshot.pipelines
    : command.kind.includes("opportunity") || command.kind === "convert_lead" ? result.snapshot.opportunities
    : command.kind.includes("task") ? result.snapshot.tasks : command.kind.includes("note") ? result.snapshot.notes : result.snapshot.leads;
  const record = records.find(item => item.id === result.entityId);
  if (!record || ("practiceId" in command && "practiceId" in record && record.practiceId !== command.practiceId)) throw new Error("Mismatched saved CRM record");
  if (command.kind === "convert_lead" && (!("leadId" in record) || record.leadId !== command.leadId)) throw new Error("Mismatched lead conversion");
  if (result.handoff && (result.handoff.opportunityId !== result.entityId || result.handoff.status !== "pending")) throw new Error("Mismatched pending enrollment handoff");
  return result;
}
export function useCrmSnapshot(identity: string, practiceId: string, allowed: boolean) {
  const api = useApi(), client = useQueryClient();
  const query = useQuery({ queryKey: crmKey(identity, practiceId), queryFn: async ({ signal }) => {
    const result = await api.get<CrmSnapshot>(crmEndpoint(practiceId), signal);
    if (result.mode !== "staging_crm" || (practiceId && !result.practices.some(item => item.id === practiceId))) throw new Error("Exact Practice unavailable");
    if (practiceId && [result.leads, result.opportunities, result.tasks, result.notes, result.activities].some(items => items.some(item => item.practiceId !== practiceId))) throw new Error("Mismatched Practice CRM response");
    return result;
  }, enabled: allowed, retry: false, gcTime: 0, refetchOnWindowFocus: false, refetchOnReconnect: false });
  function saved(result: CrmMutation) {
    if (!practiceId) client.setQueryData(crmKey(identity), result.snapshot);
    void client.invalidateQueries({ queryKey: ["crm", identity] });
    void client.invalidateQueries({ queryKey: ["practice"] });
    if (result.handoff) {
      void client.invalidateQueries({ queryKey: ["enrollment", result.handoff.enrollmentId] });
      void client.invalidateQueries({ queryKey: ["product"] });
      void client.invalidateQueries({ queryKey: ["products"] });
    }
  }
  return { query, saved };
}
